import React, { useContext, useEffect, useState } from "react";

import { SearchOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Grid,
  InputBase,
  LinearProgress,
  Link,
  Pagination,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import {
  getOrgConfigPortal,
  OrgConfigModel,
  patchOrgConfig,
  OrgConfigNotifyItem,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useNavigate, useParams } from "react-router";

import { mailTo, validateEmail } from "@/common/utils";
import { DisabledComponentDialog } from "@/components/DisabledComponent";
import { OnlineReportingContext } from "@/components/online-reporting/context";
import EmailTemplateConfig from "@/components/online-reporting/EmailTemplateConfig";
import styles from "@/components/online-reporting/ReportV2Settings.module.css";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { IUserPermission } from "@/types/typesAlerts";

interface IAddEmailOption {
  inputValue: string;
  label: string;
}

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.05),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.black, 0.1),
  },
  marginRight: theme.spacing(2),
  marginBottom: theme.spacing(2),
  width: "100%",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  padding: theme.spacing(1, 1, 1, 0),
  paddingLeft: `calc(1em + ${theme.spacing(4)})`,
}));

const RegistrySettings = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [serviceConfig, setServiceConfig] = useState<OrgConfigModel | null>(
    null,
  );
  const { reportConfigurations, fetchReportConfigurations } = useContext(
    OnlineReportingContext,
  );

  const { currentUser } = useContext(AuthStateContext);
  const [reportPermissions] = useState(
    currentUser &&
      parseUserPermissions(currentUser?.permissions as IUserPermission[]),
  );
  const canViewSettingsRegistry = reportPermissions
    ? reportPermissions.canViewSettingsRegistry
    : true;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;
  const [focusedItem, setFocusedItem] = useState<string | null>(null);

  useEffect(() => {
    async function getConfig() {
      const response = (
        await getOrgConfigPortal({ path: { org_id: orgId ?? "" } })
      ).data!;
      setServiceConfig(response);
    }
    getConfig();
  }, [orgId]);

  useEffect(() => {
    fetchReportConfigurations(orgId ?? "");
  }, [orgId]);

  useEffect(() => {
    if (serviceConfig && reportConfigurations) {
      const registryConfigurations = reportConfigurations.filter(
        (config) => config.is_registry === true,
      );

      const existingKeys = serviceConfig.notify?.notify_items.map(
        (item) => item.key,
      );

      const newConfigs = registryConfigurations.filter(
        (config) => !existingKeys?.includes(config.survey_class),
      );

      if (newConfigs.length === 0) return;

      const newNotifyItems = newConfigs.map((config) => ({
        key: config.survey_class,
        emails: [],
      }));

      const updatedNotifyItems = [
        ...(serviceConfig.notify?.notify_items || []),
        ...newNotifyItems,
      ];

      const statusNotifyItems = [
        ...(serviceConfig.notify?.status_notify_items || [
          {
            key: "all",
            emails: [],
          },
          {
            key: "approved",
            emails: [],
          },
          {
            key: "rejected",
            emails: [],
          },
          {
            key: "modification_requested",
            emails: [],
          },
        ]),
      ];

      setServiceConfig((prev) => ({
        ...prev!,
        notify: {
          notify_items: updatedNotifyItems,
          status_notify_items: statusNotifyItems,
        },
      }));
    }
  }, [serviceConfig, reportConfigurations]);

  const registryConfigurations =
    reportConfigurations?.filter((config) => config.is_registry === true) || [];

  // Filter notify items to only include those that match registry survey classes
  const filteredNotifyItems = serviceConfig?.notify?.notify_items.filter(
    (item) => {
      const isRegistryItem = registryConfigurations.some(
        (config) => config.survey_class === item.key,
      );

      const matchesSearch =
        item.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
        registryConfigurations
          .find((config) => config.survey_class === item.key)
          ?.name.toLowerCase()
          .includes(searchTerm.toLowerCase());

      return isRegistryItem && matchesSearch;
    },
  );

  const paginatedItems = filteredNotifyItems?.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  const renderStatusNotifyItem = (item: OrgConfigNotifyItem) => {
    // Remove escalated from status notify items
    if (item.key === "escalated") return null;
    return renderNotifyItem(item, true);
  };

  const renderNotifyItem = (item: OrgConfigNotifyItem, isStatus: boolean) => {
    // Only look up in registry configurations
    const config = reportConfigurations?.find(
      (config) =>
        config.survey_class === item.key && config.is_registry === true,
    );

    return (
      <Box
        key={item.key}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          width: "100%",
          padding: "4px",
        }}
      >
        <Grid
          size={3}
          sx={{
            display: "flex",
            alignItems: "center",
            width: "30%",
            marginTop: "8px",
            marginRight: "8px",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              width: "100%",
              textOverflow: "ellipsis",
            }}
          >
            {config?.name ||
              (item.key === "all" && isStatus && "ANY ACTION") ||
              (item.key === "all" && !isStatus && "ALL REGISTRY TYPES") ||
              (item.key === "approved" && "Approved") ||
              (item.key === "rejected" && "Rejected") ||
              (item.key === "modification_requested" && "Awaiting Response") ||
              item.key}
          </Typography>
        </Grid>
        <Grid
          size={9}
          sx={{
            width: "100%",
          }}
        >
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            id={`email-autocomplete-${item.key}`}
            value={item.emails}
            onChange={(event, newValues) =>
              handleEmailChange(item.key, newValues, isStatus)
            }
            sx={{ width: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                label="Emails"
                placeholder="Add email..."
                helperText={
                  focusedItem === item.key + isStatus
                    ? "Press enter to save"
                    : ""
                }
                sx={{ width: "100%" }}
                onFocus={() => setFocusedItem(item.key + isStatus)}
                onBlur={() => setFocusedItem(null)}
              />
            )}
          />
        </Grid>
      </Box>
    );
  };

  if (!canViewSettingsRegistry) {
    return (
      <DisabledComponentDialog
        isDialogOpen={true}
        handleClose={() => {
          navigate(`/`);
        }}
        title="You do not have access to view registry settings."
        message={
          <Typography>
            Please contact{" "}
            <Link
              rel="noopener noreferrer"
              target="_blank"
              href={mailTo({
                email: "support@tryrubicon.com",
                subject: "Requesting Access to View Reports",
                body: `Hello Rubicon Support Team, 

                I would like to request access to view report settings.

                Thank you. 

                Sincerely,

                ${currentUser?.first_name} ${currentUser?.last_name} 
                ${currentUser?.email}`,
              })}
              color="secondary"
            >
              support@tryrubicon.com
            </Link>{" "}
            to request access.
          </Typography>
        }
      />
    );
  }

  const handleEmailChange = async (
    key: string,
    newValues: (string | IAddEmailOption)[],
    isStatus: boolean,
  ) => {
    if (!serviceConfig) return;

    const updatedNotifyItems = serviceConfig.notify?.notify_items.map(
      (item) => {
        if (item.key === key && !isStatus) {
          const emails = newValues.map((v) =>
            typeof v === "string" ? v : v.inputValue,
          );

          if (!emails.every((email) => validateEmail(email))) {
            toast.error("Invalid email format!");
            return item;
          }

          return { ...item, emails };
        }
        return item;
      },
    );

    const updatedStatusNotifyItems =
      serviceConfig.notify?.status_notify_items?.map((item) => {
        if (item.key === key && isStatus) {
          const emails = newValues.map((v) =>
            typeof v === "string" ? v : v.inputValue,
          );

          if (!emails.every((email) => validateEmail(email))) {
            toast.error("Invalid email format!");
            return item;
          }

          return { ...item, emails };
        }
        return item;
      });

    if (updatedNotifyItems || updatedStatusNotifyItems) {
      const newConfig = (
        await patchOrgConfig({
          path: { org_id: orgId ?? "" },
          body: {
            notify: {
              notify_items: updatedNotifyItems,
              status_notify_items: updatedStatusNotifyItems,
            },
          },
        })
      ).data!;
      setServiceConfig(newConfig);
    }
  };

  return (
    <div className={styles.formContainer}>
      <Paper elevation={3} className={styles.sectionContainer}>
        <Typography
          variant="h1"
          sx={{
            fontSize: "h5.fontSize",
          }}
        >
          <b>Notification Settings</b>
        </Typography>

        <Typography variant="body2" gutterBottom>
          <b>New Registry Submission Notification</b>
        </Typography>

        <Typography variant="body2" gutterBottom>
          Manage who receives an email notification when a new registry is
          submitted. Notifications can be sent for all registry types or only
          specific registry categories.
        </Typography>

        <Search>
          <SearchIconWrapper>
            <SearchOutlined />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder="Filter by registry name..."
            inputProps={{ "aria-label": "search" }}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Search>

        {!serviceConfig ? (
          <LinearProgress sx={{ width: "100%" }} />
        ) : (
          <Box sx={{ width: "100%" }}>
            {paginatedItems?.map((item) => renderNotifyItem(item, false))}
            <Pagination
              count={Math.ceil(
                (filteredNotifyItems?.length || 0) / itemsPerPage,
              )}
              page={page}
              onChange={(event, value) => setPage(value)}
              sx={{ marginTop: 2, display: "flex", justifyContent: "center" }}
            />
          </Box>
        )}

        <Typography variant="body2" gutterBottom>
          <b>Registry Action Notification</b>
        </Typography>

        <Typography variant="body2" gutterBottom>
          Manage who receives an email notification when specific actions are
          taken on a registry.
        </Typography>
        <Box sx={{ width: "100%" }}>
          {serviceConfig?.notify?.status_notify_items?.map(
            renderStatusNotifyItem,
          )}
        </Box>
      </Paper>

      <Paper elevation={3} className={styles.sectionContainer}>
        <EmailTemplateConfig />
      </Paper>
    </div>
  );
};

export default RegistrySettings;
